import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Divider,
  Button,
  Modal,
  Spin,
  message,
  InputNumber,
  Tag,
} from "antd";
import axios from "axios";

const { Title } = Typography;

const ManageLastChanceDeals = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal state for setting deal rate
  const [dealModalVisible, setDealModalVisible] = useState(false);
  const [dealProduct, setDealProduct] = useState(null);
  const [dealRate, setDealRate] = useState();

  // Modal state for remove confirmation
  const [removeModalVisible, setRemoveModalVisible] = useState(false);
  const [removeProduct, setRemoveProduct] = useState(null);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
    checkUserActiveStatus();
  }, []);

  // Fetch products when category changes
  useEffect(() => {
    if (selectedCategory) {
      fetchProducts(selectedCategory);
    }
  }, [selectedCategory]);

  const checkUserActiveStatus = async () => {
    const tokenString = localStorage.getItem("token");

    try {
      let config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL + "api/UserAccount/GetUserAccountDetail",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
      };

      axios
        .request(config)
        .then((response) => {
          if (
            response.data.IsActive === false ||
            response.data.IsDeleted === false ||
            response.data.IsEmployee !== true // <-- Only allow if IsEmployee is true
          ) {
            alert("You are not authorized to access this page.");
            localStorage.removeItem("token");
            localStorage.removeItem("userName");
            window.location.href = "/"; // <-- Redirect to home
          }
        })
        .catch((error) => {});
    } catch (error) {}
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const tokenString = localStorage.getItem("token");
      const config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL +
          "api/ProductCategory/ListofProductCategory",
        headers: {
          Authorization: "Bearer " + tokenString,
        },
      };
      const response = await axios.request(config);
      // Sort categories by CategoryName
      const flatCategories = response.data.flatMap((cat) => cat.data);
      flatCategories.sort((a, b) =>
        a.CategoryName.localeCompare(b.CategoryName)
      );
      setCategories(flatCategories);
      if (flatCategories.length) {
        setSelectedCategory(flatCategories[0].ProductCategoryID);
      }
    } catch (error) {
      message.error("Failed to fetch categories.");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async (categoryID) => {
    setLoading(true);
    try {
      const tokenString = localStorage.getItem("token");
      const config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL +
          "/api/LastChanceDeal/GetListOfProductInDeal?CategoryID=" +
          categoryID,
        headers: {
          Authorization: "Bearer " + tokenString,
        },
      };
      const response = await axios.request(config);
      setProducts(response.data || []);
    } catch (error) {
      message.error("Failed to fetch products.");
    } finally {
      setLoading(false);
    }
  };

  // Open modal to confirm remove
  const openRemoveModal = (product) => {
    setRemoveProduct(product);
    setRemoveModalVisible(true);
  };

  // Confirm removal
  const handleConfirmRemove = async () => {
    if (!removeProduct) return;
    try {
      const tokenString = localStorage.getItem("token");
      const response = await axios.post(
        global.config.APIURL.BaseURL +
          "api/LastChanceDeal/RemoveProductfromDeal?ProductID=" +
          removeProduct.ProductID,
        {},
        {
          headers: {
            Authorization: "Bearer " + tokenString,
          },
        }
      );
      if (response.status === 200) {
        message.success("Product removed from Last Chance Deals.");
        setRemoveModalVisible(false);
        setRemoveProduct(null);
        fetchProducts(selectedCategory);
      } else {
        message.error("Failed to remove product.");
      }
    } catch (error) {
      message.error("Error removing product.");
    }
  };

  // Open modal to set deal rate
  const openDealRateModal = (product) => {
    setDealProduct(product);
    setDealRate(product.DealRate || product.SalesRate);
    setDealModalVisible(true);
  };

  // Set deal rate
  const handleSetDealRate = async () => {
    if (!dealProduct || !dealRate) return;
    try {
      const tokenString = localStorage.getItem("token");
      const response = await axios.post(
        global.config.APIURL.BaseURL +
          "api/LastChanceDeal/UpdateProductDealRate?ProductID=" +
          dealProduct.ProductID +
          "&Price=" +
          dealRate,
        {},
        {
          headers: {
            Authorization: "Bearer " + tokenString,
          },
        }
      );
      if (response.status === 200) {
        message.success("Deal rate updated.");
        setDealModalVisible(false);
        setDealProduct(null);
        fetchProducts(selectedCategory);
      } else {
        message.error("Failed to update deal rate.");
      }
    } catch (error) {
      message.error("Error updating deal rate.");
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: 8 }}>
      <Card
        style={{
          borderRadius: 16,
          boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
          background: "#fff",
          margin: 0,
        }}
        styles={{ padding: 16 }}
      >
        <Title level={2} style={{ textAlign: "center", marginBottom: 24 }}>
          Manage Last Chance Deals
        </Title>
        <Row gutter={16}>
          <Col xs={24} sm={6} md={5} lg={5} style={{ minWidth: 150 }}>
            <Card
              style={{
                marginBottom: 0,
                borderRadius: 8,
                boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                background: "#fafafa",
              }}
              styles={{ padding: 16 }}
            >
              <Title
                level={4}
                style={{
                  marginBottom: 12,
                  fontSize: 18,
                  textAlign: "left",
                }}
              >
                Categories
              </Title>
              {categories.map((cat) => (
                <Button
                  key={cat.ProductCategoryID}
                  type={
                    String(selectedCategory) === String(cat.ProductCategoryID)
                      ? "primary"
                      : "text"
                  }
                  block
                  style={{
                    marginBottom: 8,
                    textAlign: "left",
                    justifyContent: "flex-start",
                    display: "flex",
                  }}
                  onClick={() => setSelectedCategory(cat.ProductCategoryID)}
                >
                  {cat.CategoryName}
                </Button>
              ))}
            </Card>
          </Col>
          <Col xs={24} sm={18} md={19} lg={19}>
            {loading ? (
              <div style={{ textAlign: "center", padding: 40 }}>
                <Spin size="large" />
              </div>
            ) : products.length === 0 ? (
              <div style={{ textAlign: "center", color: "#888", fontSize: 18 }}>
                No products found in this category.
              </div>
            ) : (
              <Row gutter={[16, 16]}>
                {products.map((product) => (
                  <Col
                    xs={24}
                    sm={12}
                    md={8}
                    lg={6}
                    key={product.ProductID}
                    style={{ padding: 8 }}
                  >
                    <Card
                      hoverable
                      cover={
                        <img
                          alt={product.ProductName}
                          src={product.ImageUrl}
                          style={{
                            height: 180,
                            objectFit: "cover",
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                          }}
                        />
                      }
                      style={{
                        borderRadius: 8,
                        boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                        marginBottom: 0,
                      }}
                    >
                      <Title level={5} style={{ margin: 0, fontSize: 16 }}>
                        {product.ProductName}
                      </Title>
                      <div
                        style={{
                          color: "#08979c",
                          fontWeight: 600,
                          fontSize: 16,
                          margin: "8px 0",
                        }}
                      >
                        <span
                          style={{
                            textDecoration:
                              product.DealRate < product.SalesRate
                                ? "line-through"
                                : "none",
                            color:
                              product.DealRate < product.SalesRate
                                ? "#888"
                                : "#08979c",
                          }}
                        >
                          ₹{product.SalesRate?.toFixed(2)}
                        </span>
                        {product.DealRate < product.SalesRate && (
                          <span
                            style={{
                              color: "green",
                              fontWeight: "bold",
                              marginLeft: 8,
                            }}
                          >
                            ₹{product.DealRate?.toFixed(2)}
                          </span>
                        )}
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <Tag
                          color={
                            product.Status === "Available" ? "green" : "red"
                          }
                        >
                          {product.Status}
                        </Tag>
                      </div>
                      <Button
                        danger
                        block
                        onClick={() => openRemoveModal(product)}
                        style={{ marginBottom: 8 }}
                      >
                        Remove from Deal
                      </Button>
                      <Button
                        type="primary"
                        block
                        onClick={() => openDealRateModal(product)}
                      >
                        Set Deal Rate
                      </Button>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Col>
        </Row>
      </Card>
      {/* Modal for setting deal rate */}
      <Modal
        open={dealModalVisible}
        onCancel={() => setDealModalVisible(false)}
        onOk={handleSetDealRate}
        okText="Save"
        cancelText="Cancel"
        title="Set Deal Rate"
        centered
        destroyOnClose
        confirmLoading={loading}
      >
        {dealProduct && (
          <div>
            {/* Show product image */}
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <img
                src={dealProduct.ImageUrl}
                alt={dealProduct.ProductName}
                style={{
                  maxWidth: 240,
                  maxHeight: 240,
                  borderRadius: 8,
                  objectFit: "cover",
                }}
              />
            </div>
            <div>
              <b>{dealProduct.ProductName}</b>
            </div>
            <div style={{ margin: "16px 0" }}>
              <span>Sales Rate: </span>
              <b>₹{dealProduct.SalesRate?.toFixed(2)}</b>
            </div>
            <InputNumber
              min={1}
              value={dealRate}
              onChange={setDealRate}
              style={{ width: 180 }}
              prefix="₹"
              step={1}
            />
          </div>
        )}
      </Modal>
      {/* Modal for remove confirmation */}
      <Modal
        open={removeModalVisible}
        onCancel={() => setRemoveModalVisible(false)}
        onOk={handleConfirmRemove}
        okText="Yes"
        cancelText="No"
        title="Remove from Last Chance Deal"
        centered
        destroyOnClose
        confirmLoading={loading}
      >
        {removeProduct && (
          <div>
            Are you sure you want to remove
            <b> {removeProduct.ProductName} </b>
            from Last Chance Deals?
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ManageLastChanceDeals;
